import { useRef, useState, useEffect } from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import {
  queueLoadImage,
  cancelLoad,
  isImageCached,
  getCachedImage,
  retainImage,
  releaseImage,
} from 'utils/imageQueueLoader';

interface LazyAvatarProps {
  /** 图片 URL */
  src?: string;
  /** 头像大小 */
  size?: number;
  /** 头像形状 */
  shape?: 'circle' | 'square';
  /** 无图片时的 fallback 图标 */
  fallback?: React.ReactNode;
  /** 是否高优先级加载 */
  priority?: 'high' | 'normal';
}

/**
 * 懒加载头像组件
 * - 使用 IntersectionObserver 实现真正的懒加载
 * - 通过 imageQueueLoader 进行并发控制
 * - 离开视口自动取消未完成的加载
 * - 使用引用计数防止 ObjectURL 内存泄漏
 */
export function LazyAvatar({
  src,
  size = 32,
  shape = 'square',
  fallback,
  priority = 'normal',
}: LazyAvatarProps) {
  const [imageSrc, setImageSrc] = useState<string | undefined>(() => {
    // 初始化时检查缓存
    if (src && isImageCached(src)) {
      return getCachedImage(src);
    }
    return undefined;
  });
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const loadedRef = useRef(!!imageSrc);
  // 记录当前持有引用的 URL，用于释放
  const retainedUrlRef = useRef<string | null>(null);

  // IntersectionObserver 检测可见性
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { rootMargin: '100px' }, // 提前 100px 开始加载
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 进入视口后加入队列加载
  useEffect(() => {
    if (!isInView || !src || loadedRef.current) return;

    queueLoadImage(src, priority)
      .then((objectUrl) => {
        loadedRef.current = true;
        // 增加引用计数
        retainImage(src);
        retainedUrlRef.current = src;
        setImageSrc(objectUrl);
      })
      .catch(() => {
        // 加载失败时保持 fallback 状态
      });

    return () => {
      // 离开视口且未加载完成时取消
      if (!loadedRef.current && src) {
        cancelLoad(src);
      }
    };
  }, [isInView, src, priority]);

  // src 变化时重置状态
  useEffect(() => {
    // 释放之前持有的引用
    if (retainedUrlRef.current && retainedUrlRef.current !== src) {
      releaseImage(retainedUrlRef.current);
      retainedUrlRef.current = null;
    }

    if (src && isImageCached(src)) {
      setImageSrc(getCachedImage(src));
      loadedRef.current = true;
      // 增加新图片的引用计数
      retainImage(src);
      retainedUrlRef.current = src;
    } else {
      setImageSrc(undefined);
      loadedRef.current = false;
    }
  }, [src]);

  // 组件卸载时释放引用
  useEffect(() => {
    return () => {
      if (retainedUrlRef.current) {
        releaseImage(retainedUrlRef.current);
        retainedUrlRef.current = null;
      }
    };
  }, []);

  return (
    <span ref={containerRef} className="inline-block">
      {imageSrc ? (
        <Avatar shape={shape} size={size} src={imageSrc} />
      ) : (
        <Avatar shape={shape} size={size} icon={fallback || <UserOutlined />} />
      )}
    </span>
  );
}

export default LazyAvatar;
